import React, { Suspense, lazy } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SEO } from '../components/SEO';
import logoSymbol from '../assets/icons/logo-symbol.svg';
import {
  ArrowForwardRounded,
  ArrowUpwardRounded,
  AutoAwesomeRounded,
  BalanceRounded,
  SearchRounded,
  DoneRounded,
  CloseRounded,
  InfoOutlined,
  VerifiedUserOutlined,
  ExpandMoreRounded,
  MailOutlineRounded,
  StarRounded,
} from '@mui/icons-material';
import { PAY_PER_USE_PRICING, SUBSCRIPTION_PLANS } from '../config/pricing';
import {
  CONSULTATION_SCREENSHOTS,
  CONTRACT_ANALYSIS_SCREENSHOTS,
  DOCUMENTS_SCREENSHOTS,
} from '../config/screenshotCarousels';

const Footer = lazy(() => import('../components/Footer'));

/* ---- editorial design tokens (light, warm) ---- */
const c = {
  bg: '#FAF8F5',
  surface: '#FFFFFF',
  surface2: '#F3EFE9',
  ink: '#1C1A18',
  ink2: '#57524C',
  ink3: '#8A847C',
  line: '#E4DFD7',
  accent: '#E72F0D',
  accentSoft: '#FDEAE6',
  panel: '#211C19',
  panelInk: '#F5F1EB',
  panelInk2: '#A9A196',
  panelLine: '#3A322C',
  ok: '#2E6B3F',
};
const serif = "'Fraunces', 'Iowan Old Style', Georgia, serif";
const sans = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";


const SECTION_X = { xs: 3, md: 'max(24px, calc((100% - 1200px) / 2))' };

/* dot-hexagon brand mark reused from logo-symbol */
const BrandMark: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <Box component="img" src={logoSymbol} alt="PravniBOT" sx={{ width: size, height: size, display: 'block' }} />
);

const Eyebrow: React.FC<{ children: React.ReactNode; onPanel?: boolean }> = ({ children, onPanel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 26, height: 2, borderRadius: 1, bgcolor: c.accent }} />
    <Typography sx={{ fontFamily: sans, fontSize: 13, fontWeight: 600, letterSpacing: '0.14em', color: onPanel ? c.accent : c.ink3 }}>
      {children}
    </Typography>
  </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode; size?: number; onPanel?: boolean; maxWidth?: number }> = ({
  children, size = 40, onPanel, maxWidth = 760,
}) => (
  <Typography component="h2" sx={{
    fontFamily: serif, fontSize: { xs: Math.min(size, 30), md: size }, fontWeight: 600,
    lineHeight: 1.08, letterSpacing: '-0.02em', color: onPanel ? c.panelInk : c.ink, maxWidth,
  }}>
    {children}
  </Typography>
);

const CTAButton: React.FC<{ onClick: () => void; children: React.ReactNode; large?: boolean }> = ({ onClick, children, large }) => (
  <Box
    role="button" tabIndex={0} onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1.1, cursor: 'pointer', userSelect: 'none',
      px: large ? 3.75 : 3.25, py: large ? 2 : 1.9, borderRadius: '12px', bgcolor: c.accent,
      transition: 'transform 180ms ease, box-shadow 180ms ease',
      boxShadow: '0 6px 20px -8px rgba(231,47,13,0.5)',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 10px 26px -8px rgba(231,47,13,0.6)' },
      '&:focus-visible': { outline: `2px solid ${c.ink}`, outlineOffset: 3 },
    }}
  >
    <Typography sx={{ fontFamily: sans, fontSize: 16, fontWeight: 600, color: '#fff' }}>{children}</Typography>
    <ArrowForwardRounded sx={{ fontSize: 18, color: '#fff' }} />
  </Box>
);

const ScreenshotShowcase: React.FC<{ slides: { src: string; alt: string }[]; url: string; intervalMs?: number }> = ({ slides: allSlides, url, intervalMs = 3800 }) => {
  const slides = allSlides.slice(0, 5);
  const [i, setI] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const n = slides.length;
  React.useEffect(() => {
    slides.forEach((s) => { const im = new Image(); im.src = s.src; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (paused || n <= 1) return;
    const id = window.setInterval(() => setI((x) => (x + 1) % n), intervalMs);
    return () => window.clearInterval(id);
  }, [paused, i, n, intervalMs]);
  return (
    <Box onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, bgcolor: c.surface, overflow: 'hidden', boxShadow: '0 26px 64px -24px rgba(28,26,24,0.28)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, bgcolor: c.surface2, borderBottom: `1px solid ${c.line}` }}>
        {['#E4483A', '#E8B23A', '#5FB85F'].map((d) => <Box key={d} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d }} />)}
        <Box sx={{ flex: 1, maxWidth: 360, mx: 'auto', textAlign: 'center', px: 1.5, py: 0.6, borderRadius: '6px', bgcolor: c.surface }}>
          <Typography sx={{ fontSize: 12, color: c.ink3 }}>{url}</Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', width: '100%', paddingTop: '69.2%', bgcolor: c.surface2 }}>
        {slides.map((sl, idx) => (
          <Box component="img" key={idx} src={sl.src} alt={sl.alt} loading="lazy"
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'top center', pointerEvents: 'none',
              opacity: idx === i ? 1 : 0, transition: 'opacity 650ms ease' }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.5, bgcolor: c.surface }}>
        {slides.map((_, idx) => (
          <Box key={idx} role="button" tabIndex={0} aria-label={`Snímek ${idx + 1}`}
            onClick={() => setI(idx)} onKeyDown={(e) => { if (e.key === 'Enter') setI(idx); }}
            sx={{ width: idx === i ? 22 : 7, height: 7, borderRadius: 4, cursor: 'pointer',
              bgcolor: idx === i ? c.accent : c.line, transition: 'width 250ms ease, background-color 250ms ease' }} />
        ))}
      </Box>
    </Box>
  );
};

const FEATURES = [
  {
    id: 'orientace', label: 'PRÁVNÍ ORIENTACE', url: 'pravnibot.cz/orientace', price: 'od 70 Kč',
    title: 'Zeptejte se na cokoli z českého práva.',
    desc: 'Popište situaci běžnou řečí a dostanete srozumitelný přehled relevantních předpisů, s odkazy na konkrétní paragrafy a judikaturu.',
    bullets: ['Odpověď za pár minut, ne za týdny', 'Odkazy na konkrétní § a rozhodnutí soudů', 'Doplňující otázky pro přesnější odpověď'],
    slides: CONSULTATION_SCREENSHOTS,
  },
  {
    id: 'analyza', label: 'ANALÝZA SMLUV', url: 'pravnibot.cz/analyza', price: 'od 200 Kč',
    title: 'Zkontrolujeme smlouvu dřív, než ji podepíšete.',
    desc: 'Nahrajte PDF nebo Word. AI označí rizikové body, chybějící ustanovení a na co si dát pozor, s vysvětlením každého ustanovení.',
    bullets: ['Rizika seřazená podle závažnosti', 'Vysvětlení sporných ustanovení', 'Poznámky a export výsledku'],
    slides: CONTRACT_ANALYSIS_SCREENSHOTS,
  },
  {
    id: 'generovani', label: 'GENEROVÁNÍ SMLUV', url: 'pravnibot.cz/smlouvy', price: 'od 100 Kč',
    title: 'Vzor smlouvy na míru podle platného práva.',
    desc: 'Průvodce vás krok za krokem provede zadáním a vygeneruje návrh dokumentu podle aktuálních předpisů, ne zastaralý vzor. Před podpisem doporučujeme kontrolu.',
    bullets: ['Návrh dle aktuálního znění předpisů', 'Přehledný průvodce krok za krokem', 'Export do PDF nebo DOCX'],
    slides: DOCUMENTS_SCREENSHOTS,
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const goRegister = () => navigate('/login?tab=register');
  const location = useLocation();
  React.useEffect(() => {
    if (!location.hash) return;
    const id = window.setTimeout(() => {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 60);
    return () => window.clearTimeout(id);
  }, [location.hash, location.key]);

  const steps = [
    { n: '01', title: 'Zeptáte se', desc: 'Popište situaci běžnou řečí, jako byste psali známému. Žádný právní jazyk.' },
    { n: '02', title: 'AI prohledá právo', desc: 'Projde aktuální znění zákonů a relevantní judikaturu k vašemu konkrétnímu případu.' },
    { n: '03', title: 'Dostanete odpověď', desc: 'Srozumitelně a k věci, s odkazy na konkrétní paragrafy a zdroje, které si ověříte.' },
  ];

  const compareRows = [
    ['Aktuální české zákony', 'Trénink zamrzlý v čase', 'Denně aktualizované znění'],
    ['Citace paragrafů', 'Vymýšlí čísla § i judikáty', 'Odkaz na platné znění'],
    ['Judikatura NS · NSS · ÚS', 'Nezná českou praxi', 'Prohledává rozhodnutí soudů'],
    ['Mlčenlivost a soukromí', 'Chaty mohou skončit u soudu', 'GDPR, data hostovaná v EU'],
    ['Výstup', 'Jen text v okně', 'Návrh smlouvy, analýza rizik, export'],
  ];

  const testimonials = [
    { init: 'JK', quote: 'Nevěděla jsem, jestli je výpověď z nájmu platná. Za pět minut jsem měla jasno, i s odkazem na konkrétní paragraf.', name: 'Jana K.', role: 'OSVČ, Brno' },
    { init: 'PM', quote: 'Zkontroloval mi nájemní smlouvu a našel smluvní pokutu, které bych si nikdy nevšiml. Ušetřil mi to velký problém.', name: 'Petr M.', role: 'Živnostník, Praha' },
    { init: 'LH', quote: 'Konečně srozumitelně. Nemusím platit hodinu právníkovi za otázku, kterou vyřeším za korunu.', name: 'Lucie H.', role: 'HR manažerka, Ostrava' },
  ];

  const faqs = [
    { q: 'Je PravniBOT advokátní kancelář?', a: 'Ne. PravniBOT poskytuje orientaci v platných předpisech a judikatuře. Nenahrazuje individuální právní poradenství advokáta a u složitých případů doporučí, kdy se na advokáta obrátit.' },
    { q: 'Kolik stojí AI právník?', a: 'Orientace v právu stojí od 70 Kč, kontrola smlouvy od 200 Kč, generování dokumentu od 100 Kč. Platí se kredity (1 kredit = 1 Kč vč. DPH), na startu dostanete 300 kreditů zdarma.' },
    { q: 'Je právní poradna online zdarma?', a: 'Vyzkoušení je zdarma: po registraci získáte 300 kreditů bez platební karty. Poté platíte jen za dokončené dotazy, orientace vyjde na 70 Kč.' },
    { q: 'Umí PravniBOT zkontrolovat nájemní nebo kupní smlouvu?', a: 'Ano. Nahrajete smlouvu v PDF nebo Wordu a AI označí rizikové body, chybějící ustanovení a na co si dát pozor, s odkazy na paragrafy. Kontrola smlouvy stojí od 200 Kč.' },
    { q: 'Vygeneruje PravniBOT vzor smlouvy nebo plnou moc?', a: 'Ano. Generátor vytvoří návrh smlouvy nebo plné moci podle platného práva, ne zastaralý vzor. Před podpisem doporučujeme kontrolu. Generování stojí od 100 Kč.' },
    { q: 'Odkud berete data?', a: 'Ze Sbírky zákonů a z judikatury Nejvyššího soudu, NSS a Ústavního soudu. Zákony aktualizujeme denně, judikaturu týdně.' },
    { q: 'Jsou moje dokumenty v bezpečí?', a: 'Ano. Data hostujeme v EU v souladu s GDPR, přenos i uložení jsou šifrované a vaše dotazy netrénují AI modely.' },
  ];

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PravniBOT.cz',
    url: 'https://pravnibot.cz/',
    logo: 'https://pravnibot.cz/logo512.png',
    description: 'AI průvodce českým právem: orientace v předpisech a judikatuře, kontrola a generování smluv. Není advokátní kancelář a nenahrazuje advokáta.',
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="AI právník a právní poradna online | PravniBOT.cz"
        description="PravniBOT je AI právník pro orientaci v českém právu. Zeptejte se běžnou řečí, zkontrolujte smlouvu nebo vygenerujte vzor. Odpovědi s odkazy na paragrafy. 300 kreditů zdarma."
        canonicalUrl="https://pravnibot.cz/"
        appendSiteName={false}
      >
        <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </SEO>

      <Box sx={{ fontFamily: sans, bgcolor: c.bg, color: c.ink, width: '100%', overflowX: 'hidden' }}>

        {/* ===== HERO ===== */}
        <Box sx={{ px: SECTION_X, pt: { xs: 'calc(60px + 32px)', md: 'calc(72px + 56px)' }, pb: { xs: 8, md: 11 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 7 }, alignItems: { md: 'flex-start' } }}>
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 544 } }}>
              <Box sx={{ mb: 2.75 }}><Eyebrow>AI PRÁVNÍK PRO ČESKÉ PRÁVO</Eyebrow></Box>
              <Typography component="h1" sx={{ fontFamily: serif, fontSize: { xs: 38, md: 56 }, fontWeight: 600, lineHeight: 1.04, letterSpacing: '-0.022em', color: c.ink }}>
                AI právník, který vás v právu zorientuje za pár minut.
              </Typography>
              <Typography sx={{ mt: 2.75, fontSize: 18, lineHeight: 1.5, color: c.ink2, maxWidth: 490 }}>
                Zeptejte se běžnou řečí na svůj právní problém. Dostanete srozumitelnou odpověď s odkazy na platné paragrafy a aktuální judikaturu, ne obecné fráze.
              </Typography>
              <Box sx={{ mt: 4.25, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <CTAButton onClick={goRegister}>Vyzkoušet zdarma</CTAButton>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.ink }}>300 kreditů na startu</Typography>
                  <Typography sx={{ fontSize: 13, color: c.ink3 }}>bez platební karty</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 3.75, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <InfoOutlined sx={{ fontSize: 16, color: c.ink3, mt: '2px' }} />
                <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: c.ink3, maxWidth: 440 }}>
                  Poskytujeme orientaci v předpisech, ne právní poradenství. PravniBOT není advokátní kancelář a nenahrazuje advokáta.
                </Typography>
              </Box>
            </Box>

            {/* demo window */}
            <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
              <Box sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, bgcolor: c.surface, overflow: 'hidden', boxShadow: '0 24px 60px -18px rgba(28,26,24,0.18)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.25, py: 1.75, bgcolor: c.surface2, borderBottom: `1px solid ${c.line}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                    <BrandMark size={22} />
                    <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 700, color: c.ink }}>PravniBOT.cz</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.625, borderRadius: '20px', bgcolor: c.surface, border: `1px solid ${c.line}` }}>
                    <AutoAwesomeRounded sx={{ fontSize: 13, color: c.accent }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: c.ink2 }}>Odpovídá AI</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{ maxWidth: 372, px: 2, py: 1.6, borderRadius: '14px 14px 4px 14px', bgcolor: c.accentSoft }}>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.45, color: c.ink }}>Může mi pronajímatel zvýšit nájem o 20 % ze dne na den?</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.4, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: c.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                      <BrandMark size={18} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.55, color: c.ink }}>
                        Ne bez dohody. Nájem lze jednostranně zvýšit nejvýše o 20 % za tři roky a pronajímatel to musí navrhnout písemně. Vy máte dva měsíce na vyjádření. Pokud nesouhlasíte, o zvýšení rozhoduje soud.
                      </Typography>
                      <Typography sx={{ mt: 1.5, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.05em', color: c.ink3 }}>ZDROJE</Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.9, flexWrap: 'wrap' }}>
                        {['§ 2249 OZ', '§ 2250 OZ', 'NS 26 Cdo 1259/2021'].map((s) => (
                          <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.6, borderRadius: '7px', bgcolor: c.surface2, border: `1px solid ${c.line}` }}>
                            <BalanceRounded sx={{ fontSize: 12, color: c.accent }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 500, color: c.ink2 }}>{s}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.6, bgcolor: c.surface, borderTop: `1px solid ${c.line}` }}>
                  <Typography sx={{ fontSize: 14, color: c.ink3 }}>Zeptejte se na vlastní situaci…</Typography>
                  <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowUpwardRounded sx={{ fontSize: 17, color: '#fff' }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ===== TRUST BAR ===== */}
        <Box sx={{ px: SECTION_X, py: 3.25, bgcolor: c.surface2, borderTop: `1px solid ${c.line}`, borderBottom: `1px solid ${c.line}` }}>
          <Box sx={{ display: { xs: 'grid', md: 'flex' }, gridTemplateColumns: { xs: '1fr 1fr', md: 'none' }, flexWrap: 'wrap', gap: { xs: 3, md: 0 }, justifyContent: 'space-between', alignItems: 'center' }}>
            {[['2 400+', 'Aktuálních předpisů'], ['Denně', 'Aktualizace zákonů'], ['Týdně', 'Nová judikatura NS · NSS · ÚS'], ['GDPR', 'Data hostovaná v EU']].map(([v, l], i, arr) => (
              <React.Fragment key={v}>
                <Box>
                  <Typography sx={{ fontFamily: serif, fontSize: 27, fontWeight: 600, letterSpacing: '-0.01em', color: c.ink }}>{v}</Typography>
                  <Typography sx={{ fontSize: 13.5, color: c.ink3 }}>{l}</Typography>
                </Box>
                {i < arr.length - 1 && <Box sx={{ width: '1px', height: 42, bgcolor: c.line, display: { xs: 'none', md: 'block' } }} />}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* ===== HOW IT WORKS ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 8, md: 11 } }}>
          <Eyebrow>JAK FUNGUJE PRÁVNÍ PORADNA ONLINE</Eyebrow>
          <Box sx={{ mt: 1.75, mb: 5.5 }}><SectionTitle maxWidth={720}>Od dotazu k odpovědi s odkazy na zákony ve třech krocích.</SectionTitle></Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            {steps.map((s) => (
              <Box key={s.n}>
                <Box sx={{ height: 148, borderRadius: '12px', border: `1px solid ${c.line}`, bgcolor: c.surface, mb: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                  {s.n === '01' && <Box sx={{ width: '100%', px: 1.75, py: 1.5, borderRadius: '10px', bgcolor: c.surface2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13.5, color: c.ink2 }}>Dostal jsem výpověď z nájmu…</Typography>
                    <Box sx={{ width: 2, height: 16, bgcolor: c.accent }} />
                  </Box>}
                  {s.n === '02' && <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}><SearchRounded sx={{ fontSize: 15, color: c.accent }} /><Typography sx={{ fontSize: 12.5, fontWeight: 500, color: c.ink3 }}>Prohledávám předpisy…</Typography></Box>
                    {['§ 2288 OZ — výpověď nájmu', '§ 2291 OZ — výpovědní důvody'].map((t) => (
                      <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.4, py: 1.1, borderRadius: '8px', bgcolor: c.surface2 }}>
                        <BalanceRounded sx={{ fontSize: 13, color: c.accent }} /><Typography sx={{ fontSize: 12.5, color: c.ink2 }}>{t}</Typography>
                      </Box>
                    ))}
                  </Box>}
                  {s.n === '03' && <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.5, color: c.ink, mb: 1.25 }}>Výpověď musí být písemná a s uvedeným důvodem. Máte nárok na výpovědní dobu.</Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.6, borderRadius: '7px', bgcolor: c.accentSoft }}>
                      <BalanceRounded sx={{ fontSize: 12, color: c.accent }} /><Typography sx={{ fontSize: 12, fontWeight: 500, color: c.accent }}>§ 2286 OZ</Typography>
                    </Box>
                  </Box>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 0.75 }}>
                  <Typography sx={{ fontFamily: serif, fontSize: 18, fontWeight: 600, color: c.accent }}>{s.n}</Typography>
                  <Typography sx={{ fontSize: 19, fontWeight: 600, color: c.ink }}>{s.title}</Typography>
                </Box>
                <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: c.ink2 }}>{s.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ===== FEATURES (per-tool sections) ===== */}
        {FEATURES.map((f, idx) => (
          <Box key={f.id} id={f.id} sx={{ px: SECTION_X, py: { xs: 7, md: 10 }, bgcolor: idx % 2 === 1 ? c.surface2 : c.bg, scrollMarginTop: '84px' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: idx % 2 === 0 ? 'row' : 'row-reverse' }, gap: { xs: 4, md: 7 }, alignItems: 'center' }}>
              <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                <ScreenshotShowcase slides={f.slides} url={f.url} />
              </Box>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Eyebrow>{f.label}</Eyebrow>
                <Box sx={{ mt: 1.75 }}><SectionTitle size={34} maxWidth={520}>{f.title}</SectionTitle></Box>
                <Typography sx={{ mt: 1.75, fontSize: 16.5, lineHeight: 1.55, color: c.ink2, maxWidth: 480 }}>{f.desc}</Typography>
                <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {f.bullets.map((b) => (
                    <Box key={b} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                      <DoneRounded sx={{ fontSize: 18, color: c.accent, mt: '1px', flex: '0 0 auto' }} />
                      <Typography sx={{ fontSize: 15, color: c.ink }}>{b}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 3.25, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <CTAButton onClick={goRegister}>Vyzkoušet zdarma</CTAButton>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: c.ink3 }}>{f.price}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        {/* ===== STATEMENT (dark) ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 8, md: 9.5 }, bgcolor: c.panel }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 3, md: 5.5 } }}>
            <Typography sx={{ fontFamily: serif, fontSize: { xs: 90, md: 150 }, fontWeight: 600, color: c.accent, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>§</Typography>
            <Box>
              <Typography sx={{ fontFamily: serif, fontStyle: 'italic', fontSize: { xs: 30, md: 42 }, fontWeight: 500, lineHeight: 1.12, letterSpacing: '-0.015em', color: c.panelInk }}>
                „Neznalost zákona neomlouvá.“
              </Typography>
              <Typography sx={{ mt: 2.25, fontSize: 17, lineHeight: 1.55, color: c.panelInk2, maxWidth: 660 }}>
                Jenže dostat se k němu srozumitelně bylo vždycky drahé a pomalé. Zeptat se na svá práva by mělo být stejně snadné jako napsat zprávu. Přesně to měníme.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ===== WHY NOT CHATGPT (dark) ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 8, md: 11 }, bgcolor: c.panel }}>
          <Eyebrow onPanel>PROČ NE OBYČEJNÝ CHATGPT</Eyebrow>
          <Box sx={{ mt: 1.75 }}><SectionTitle size={46} onPanel maxWidth={760}>Obecná AI zní právně. Neplatí to samé jako znát české právo.</SectionTitle></Box>
          <Typography sx={{ mt: 1.75, mb: 5, fontSize: 16, lineHeight: 1.5, color: c.panelInk2, maxWidth: 640 }}>
            Ve zkouškách České advokátní komory obecné modely propadají a vymýšlejí si judikáty. PravniBOT staví každou odpověď na platném znění předpisu.
          </Typography>
          <Box sx={{ overflowX: 'auto', mx: { xs: -3, md: 0 }, px: { xs: 3, md: 0 } }}>
          <Box sx={{ minWidth: { xs: 560, md: 'auto' }, borderRadius: '14px', border: `1px solid ${c.panelLine}`, overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '148px 1fr 1fr', md: '360px 1fr 1fr' }, bgcolor: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${c.panelLine}` }}>
              <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2 }}><Typography sx={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.05em', color: c.panelInk2 }}>SCHOPNOST</Typography></Box>
              <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2 }}><Typography sx={{ fontSize: 14, fontWeight: 600, color: c.panelInk2 }}>Obecná AI (ChatGPT)</Typography></Box>
              <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, bgcolor: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.accent }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: c.panelInk }}>PravniBOT</Typography>
              </Box>
            </Box>
            {compareRows.map((r, i) => (
              <Box key={r[0]} sx={{ display: 'grid', gridTemplateColumns: { xs: '148px 1fr 1fr', md: '360px 1fr 1fr' }, borderBottom: i < compareRows.length - 1 ? `1px solid ${c.panelLine}` : 'none' }}>
                <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, display: 'flex', alignItems: 'center' }}><Typography sx={{ fontSize: 15, fontWeight: 600, color: c.panelInk }}>{r[0]}</Typography></Box>
                <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <CloseRounded sx={{ fontSize: 17, color: c.panelInk2, flex: '0 0 auto' }} /><Typography sx={{ fontSize: 14.5, lineHeight: 1.4, color: c.panelInk2 }}>{r[1]}</Typography>
                </Box>
                <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, bgcolor: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <DoneRounded sx={{ fontSize: 17, color: c.accent, flex: '0 0 auto' }} /><Typography sx={{ fontSize: 14.5, lineHeight: 1.4, fontWeight: 500, color: c.panelInk }}>{r[2]}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          </Box>
        </Box>

        {/* ===== TESTIMONIALS ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 8, md: 11 }, bgcolor: c.surface2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'flex-end' }, gap: 3, mb: 5 }}>
            <Box>
              <Eyebrow>OD LIDÍ, KTEŘÍ HO POUŽÍVAJÍ</Eyebrow>
              <Box sx={{ mt: 1.75 }}><SectionTitle maxWidth={640}>Jasno v právu, za pár korun a pár minut.</SectionTitle></Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75, borderRadius: '12px', bgcolor: c.surface, border: `1px solid ${c.line}` }}>
              <Typography sx={{ fontFamily: serif, fontSize: 30, fontWeight: 600, color: c.ink }}>4,9</Typography>
              <Box>
                <Box sx={{ display: 'flex', gap: 0.25 }}>{[0, 1, 2, 3, 4].map((i) => <StarRounded key={i} sx={{ fontSize: 13, color: c.accent }} />)}</Box>
                <Typography sx={{ fontSize: 12.5, color: c.ink3 }}>spokojenost uživatelů</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.75 }}>
            {testimonials.map((t) => (
              <Box key={t.name} sx={{ pt: 3 }}>
                <Box sx={{ width: 48, height: 3, borderRadius: 1, bgcolor: c.accent, mb: 2.25 }} />
                <Box sx={{ display: 'flex', gap: 0.4, mb: 2 }}>{[0, 1, 2, 3, 4].map((i) => <StarRounded key={i} sx={{ fontSize: 15, color: c.accent }} />)}</Box>
                <Typography sx={{ fontFamily: serif, fontSize: 17.5, lineHeight: 1.4, letterSpacing: '-0.005em', color: c.ink, mb: 2.25 }}>„{t.quote}“</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: c.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: c.accent }}>{t.init}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.ink }}>{t.name}</Typography>
                    <Typography sx={{ fontSize: 13, color: c.ink3 }}>{t.role}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ===== PRICING ===== */}
        <Box id="cenik" sx={{ scrollMarginTop: '84px', px: SECTION_X, py: { xs: 8, md: 11 } }}>
          <Eyebrow>CENÍK</Eyebrow>
          <Box sx={{ mt: 1.75 }}><SectionTitle size={34} maxWidth={720}>Platíte za výsledek. Kredity nepropadají.</SectionTitle></Box>
          <Typography sx={{ mt: 1.75, mb: 4, fontSize: 16, color: c.ink2 }}>
            Kolik stojí AI právník? Orientace od 70 Kč, kontrola smlouvy od 200 Kč. 1 kredit = 1 Kč vč. DPH, platíte až po dokončení.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2.5 }}>
            {PAY_PER_USE_PRICING.map((a) => (
              <Box key={a.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2.75, py: 2.25, borderRadius: '14px', border: `1px solid ${c.line}`, bgcolor: c.surface }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 500, color: c.ink }}>{a.name}</Typography>
                <Typography sx={{ fontFamily: serif, fontSize: 22, fontWeight: 600, color: c.ink, whiteSpace: 'nowrap' }}>{a.priceCZK} Kč</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
            {SUBSCRIPTION_PLANS.map((p) => {
              const hl = p.key === 'standard';
              return (
                <Box key={p.key} sx={{ borderRadius: '18px', border: hl ? `2px solid ${c.accent}` : `1px solid ${c.line}`, bgcolor: c.surface, p: 3.75, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: hl ? c.accent : c.ink2 }}>{p.name}</Typography>
                    {hl && <Box sx={{ px: 1.4, py: 0.6, borderRadius: '20px', bgcolor: c.accent }}><Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#fff' }}>Nejoblíbenější</Typography></Box>}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, mt: 2.25, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: serif, fontSize: 44, fontWeight: 600, letterSpacing: '-0.02em', color: c.ink, lineHeight: 1 }}>
                      {'priceCZK' in p ? p.priceCZK : (p as any).priceLabel}
                    </Typography>
                    {'priceCZK' in p && <Box sx={{ pb: 1 }}><Typography sx={{ fontSize: 15, fontWeight: 600, color: c.ink2, lineHeight: 1.1 }}>Kč</Typography><Typography sx={{ fontSize: 13, color: c.ink3 }}>/ měsíc</Typography></Box>}
                  </Box>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 500, color: c.ink }}>
                    {'credits' in p ? `${(p.credits as number).toLocaleString('cs-CZ')} kreditů měsíčně` : (p as any).creditsLabel}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: c.ink3 }}>{p.userLabel}</Typography>
                  <Box sx={{ height: '1px', bgcolor: c.line, my: 2.25 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4, mb: 2.75 }}>
                    {p.usageExamples.map((f) => (
                      <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                        <DoneRounded sx={{ fontSize: 16, color: c.accent, flex: '0 0 auto' }} /><Typography sx={{ fontSize: 14, color: c.ink2 }}>{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box role="button" tabIndex={0} onClick={() => navigate(p.key === 'pro' ? '/kontakty' : '/pricing')}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(p.key === 'pro' ? '/kontakty' : '/pricing'); }}
                    sx={{ mt: 'auto', textAlign: 'center', py: 1.6, borderRadius: '11px', cursor: 'pointer', bgcolor: hl ? c.accent : 'transparent', border: hl ? 'none' : `1px solid ${c.line}`, '&:hover': { bgcolor: hl ? c.accent : c.surface2 } }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: hl ? '#fff' : c.ink }}>{p.key === 'pro' ? 'Kontaktovat' : 'Vybrat plán'}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ===== SECURITY + DISCLAIMER ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 6, md: 9 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, borderRadius: '18px', border: `1px solid ${c.line}`, bgcolor: c.surface, overflow: 'hidden' }}>
            {[
              { icon: VerifiedUserOutlined, title: 'Bezpečnost dat', items: [['check', 'Data hostovaná v EU, v souladu s GDPR.'], ['check', 'Šifrovaný přenos i uložení dotazů a dokumentů.'], ['check', 'Vaše dotazy a smlouvy netrénují AI modely.']] },
              { icon: BalanceRounded, title: 'Co PravniBOT je a co ne', items: [['info', 'Poskytuje orientaci v předpisech, nikoli individuální právní poradenství, a nenahrazuje advokáta.'], ['info', 'Není advokátní kancelář. U složitých případů doporučí obrátit se na advokáta.'], ['ai', 'Odpovídá umělá inteligence, obsah je dle EU AI Act označen jako vygenerovaný AI.']] },
            ].map((col, ci) => {
              const Icon = col.icon;
              return (
              <Box key={col.title} sx={{ flex: 1, p: 5, borderLeft: { md: ci === 1 ? `1px solid ${c.line}` : 'none' }, borderTop: { xs: ci === 1 ? `1px solid ${c.line}` : 'none', md: 'none' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.75 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: c.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 18, color: c.accent }} />
                  </Box>
                  <Typography sx={{ fontFamily: serif, fontSize: 22, fontWeight: 600, color: c.ink }}>{col.title}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                  {col.items.map((it) => (
                    <Box key={it[1]} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4 }}>
                      {it[0] === 'check' ? <DoneRounded sx={{ fontSize: 17, color: c.accent, mt: '2px' }} /> : it[0] === 'ai' ? <AutoAwesomeRounded sx={{ fontSize: 17, color: c.accent, mt: '2px' }} /> : <InfoOutlined sx={{ fontSize: 17, color: c.accent, mt: '2px' }} />}
                      <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: c.ink2 }}>{it[1]}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              );
            })}
          </Box>
        </Box>

        {/* ===== FAQ ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 8, md: 11 }, bgcolor: c.surface2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 4, md: 7 }, alignItems: 'flex-start' }}>
            <Box sx={{ width: { xs: '100%', md: 340 }, flex: '0 0 auto' }}>
              <Eyebrow>ČASTÉ DOTAZY</Eyebrow>
              <Box sx={{ mt: 2 }}><SectionTitle size={36} maxWidth={300}>Než začnete.</SectionTitle></Box>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MailOutlineRounded sx={{ fontSize: 16, color: c.accent }} />
                <Typography sx={{ fontSize: 14.5, fontWeight: 500, color: c.ink2 }}>Nenašli jste odpověď? Napište nám.</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
              {faqs.map((f, i) => (
                <Box key={f.q} component="details" sx={{ borderTop: i === 0 ? `1px solid ${c.line}` : 'none', borderBottom: `1px solid ${c.line}`, py: 2.75, '&[open] .faq-chev': { transform: 'rotate(180deg)', color: c.accent } }}>
                  <Box component="summary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, cursor: 'pointer', listStyle: 'none', '&::-webkit-details-marker': { display: 'none' } }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 600, color: c.ink }}>{f.q}</Typography>
                    <ExpandMoreRounded className="faq-chev" sx={{ fontSize: 20, color: c.ink3, transition: 'transform 200ms ease', flex: '0 0 auto' }} />
                  </Box>
                  <Typography sx={{ mt: 1.5, fontSize: 15, lineHeight: 1.55, color: c.ink2, maxWidth: 660 }}>{f.a}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* ===== FINAL CTA (dark) ===== */}
        <Box sx={{ px: SECTION_X, py: { xs: 9, md: 12 }, bgcolor: c.panel, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 2.5 }}><Eyebrow onPanel>ZAČNĚTE ZDARMA</Eyebrow></Box>
            <Typography component="h2" sx={{ fontFamily: serif, fontSize: { xs: 34, md: 48 }, fontWeight: 600, lineHeight: 1.06, letterSpacing: '-0.02em', color: c.panelInk, maxWidth: 820 }}>
              Získejte jasno ve svém právním problému ještě dnes.
            </Typography>
            <Typography sx={{ mt: 2.25, fontSize: 17, lineHeight: 1.5, color: c.panelInk2, maxWidth: 560 }}>
              Zeptejte se běžnou řečí a dostanete srozumitelnou odpověď s odkazy na zdroje. 300 kreditů na startu, bez platební karty.
            </Typography>
            <Box sx={{ mt: 4.25, display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CTAButton onClick={goRegister} large>Vyzkoušet zdarma</CTAButton>
              <Typography sx={{ fontSize: 14, color: c.panelInk2 }}>Bez karty · kredity nepropadají</Typography>
            </Box>
            <Box sx={{ mt: 3.25, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlined sx={{ fontSize: 14, color: c.panelInk2 }} />
              <Typography sx={{ fontSize: 13, color: c.panelInk2 }}>Orientace v předpisech, nikoli právní poradenství. Odpovídá AI.</Typography>
            </Box>
          </Box>
        </Box>

        <Suspense fallback={null}><Footer /></Suspense>
      </Box>
    </>
  );
};

export default HomePage;
